import {
	BUILDER_THEME_SCOPE_CLASS,
	ENTRY_TYPOGRAPHY_SCOPE_SELECTOR,
	isCssCascadeKeyword,
	readStyleScalar,
	stripCssImportant,
	THEME_TYPOGRAPHY_IMPORTANT_PROPS,
} from "../theme/tenantThemeCss";
import {
	isSiteSansFontFamilyToken,
	resolveTypographyFontFamilyForCss,
	TYPOGRAPHY_SCOPED_EXCLUDES,
} from "../theme/typographyTokens";
import type { ElementNode, ElementStyle } from "../types";

function escapeAttr(id: string): string {
	return id.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function nodeIdForSelector(nodeId: string | null | undefined): string | null {
	if (typeof nodeId !== "string") {
		return null;
	}
	const trimmed = nodeId.trim();
	return trimmed.length > 0 ? trimmed : null;
}

function kebabCase(prop: string): string {
	return prop.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`);
}

function tokenValue(token: unknown): string | undefined {
	const scalar = readStyleScalar(token).trim();
	return scalar || undefined;
}

/**
 * Selector that beats Theme Manager tag rules when both use `!important`.
 *
 * Theme tag rules look like
 * `[data-entry-typography] h2:not([data-pb-mega-menu] *):not([data-pb-custom-code-root] *)`
 * ≈ (0, 3, 1). A bare `[data-pb-node]` branch ≈ (0, 3, 0) loses to the tag
 * type selector. Prefix `html body`, mirror {@link TYPOGRAPHY_SCOPED_EXCLUDES},
 * and target `[data-pb-node]` ≈ (0, 4, 2) — wins on canvas and Region.
 *
 * Global chrome (`[data-global-slot]`) is intentionally omitted: site Sans is
 * forced there via `buildGlobalChromeFontFamilyCss`; element overrides must not
 * re-introduce legacy Times stacks into footer/header.
 */
export function pbNodeBeatThemeSelector(
	nodeId: string | null | undefined
): string {
	const id = nodeIdForSelector(nodeId);
	// A missing id used to throw inside escapeAttr and take down every page
	// that renders the tree (header/footer have no per-slot error boundary).
	if (!id) {
		return "";
	}
	const sel = `[data-pb-node="${escapeAttr(id)}"]${TYPOGRAPHY_SCOPED_EXCLUDES}`;
	return `html body .${BUILDER_THEME_SCOPE_CLASS} ${sel},html body ${ENTRY_TYPOGRAPHY_SCOPE_SELECTOR} ${sel}`;
}

/**
 * Append a pseudo-class (e.g. `:hover`) to every branch of
 * {@link pbNodeBeatThemeSelector}.
 *
 * Same trap as {@link pbNodeBeatThemeDescendantSelector}: that helper returns a
 * comma-separated list, so `${sel}:hover` attaches the pseudo to the LAST branch only —
 * and the first branch then applies the hover declarations at rest, painting the element
 * permanently in its hover colour.
 */
export function pbNodeBeatThemePseudoSelector(
	nodeId: string,
	pseudo: string
): string {
	return pbNodeBeatThemeSelector(nodeId)
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean)
		.map((part) => `${part}${pseudo}`)
		.join(",");
}

/**
 * Append a descendant (e.g. `.pb-faq-q`) to every branch of
 * {@link pbNodeBeatThemeSelector}. Needed because that helper returns a
 * comma-separated list — naive string concat only targets the last branch.
 *
 * Comma-separated descendants are expanded so each fragment gets the full
 * beat-theme prefix (e.g. `label,label a` → two complete selector lists).
 */
export function pbNodeBeatThemeDescendantSelector(
	nodeId: string,
	descendant: string
): string {
	const roots = pbNodeBeatThemeSelector(nodeId)
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);
	const children = descendant
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);
	if (children.length === 0) {
		return roots.join(",");
	}
	return roots
		.flatMap((root) => children.map((child) => `${root} ${child}`))
		.join(",");
}

/**
 * Prefix every branch of {@link pbNodeBeatThemeSelector} with an ancestor
 * (e.g. `[data-builder-viewport="mobile"]`). The mirror of
 * {@link pbNodeBeatThemeDescendantSelector}: naive string concat gates only the
 * FIRST branch, so the remaining branch stays ungated and a preview-only rule
 * then applies at every width on the published page.
 */
export function pbNodeBeatThemeAncestorSelector(
	nodeId: string,
	ancestor: string
): string {
	return pbNodeBeatThemeSelector(nodeId)
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean)
		.map((part) => `${ancestor} ${part}`)
		.join(",");
}

/** Build a declaration block for typography props Theme Manager forces with `!important`. */
export function typographyImportantDeclarationBlock(
	css: Record<string, unknown> | undefined
): string {
	if (!css) {
		return "";
	}
	const parts: string[] = [];
	for (const key of THEME_TYPOGRAPHY_IMPORTANT_PROPS) {
		const raw = css[key];
		let value: string | undefined;
		if (
			typeof raw === "number" &&
			key === "fontWeight" &&
			Number.isFinite(raw)
		) {
			value = String(raw);
		} else if (typeof raw !== "number") {
			const scalar = readStyleScalar(raw).trim();
			if (scalar) {
				value = stripCssImportant(scalar);
			}
		}
		if (!value) {
			continue;
		}
		// `font-size: inherit !important` beats Theme Manager tag rules and
		// prevents theme defaults from showing — skip cascade keywords.
		if (isCssCascadeKeyword(value)) {
			continue;
		}
		if (key === "fontFamily") {
			value = resolveTypographyFontFamilyForCss(value);
			// Site Sans is the Theme Manager region basis — do not emit a
			// beat-theme override so per-tag typography groups can apply.
			if (isSiteSansFontFamilyToken(value)) {
				continue;
			}
		}
		parts.push(`${kebabCase(key)}:${value}!important`);
	}
	return parts.join(";");
}

function walkNodes(
	nodes: ElementNode[] | undefined,
	visit: (n: ElementNode) => void
): void {
	if (!nodes) {
		return;
	}
	for (const n of nodes) {
		visit(n);
		const ch =
			"children" in n && Array.isArray(n.children)
				? (n.children as ElementNode[])
				: undefined;
		walkNodes(ch, visit);
	}
}

function typographyFromTokenizedStyle(
	style: ElementStyle | undefined
): Record<string, unknown> {
	const typo = style?.typography;
	if (!typo) {
		return {};
	}
	const out: Record<string, unknown> = {};
	const fontFamily = tokenValue(typo.fontFamily);
	if (fontFamily) {
		out.fontFamily = fontFamily;
	}
	const fontSize = tokenValue(typo.fontSize);
	if (fontSize) {
		out.fontSize = fontSize;
	}
	const fontWeight = tokenValue(typo.fontWeight);
	if (fontWeight) {
		out.fontWeight = fontWeight;
	}
	const lineHeight = tokenValue(typo.lineHeight);
	if (lineHeight) {
		out.lineHeight = lineHeight;
	}
	const letterSpacing = tokenValue(typo.letterSpacing);
	if (letterSpacing) {
		out.letterSpacing = letterSpacing;
	}
	const color = tokenValue(typo.color);
	if (color) {
		out.color = color;
	}
	if (typeof typo.textTransform === "string" && typo.textTransform.trim()) {
		out.textTransform = typo.textTransform.trim();
	}
	const textAlign = tokenValue(typo.textAlign);
	if (textAlign) {
		out.textAlign = textAlign;
	}
	if (typeof typo.fontStyle === "string" && typo.fontStyle.trim()) {
		out.fontStyle = typo.fontStyle.trim();
	}
	if (typeof typo.textDecoration === "string" && typo.textDecoration.trim()) {
		out.textDecoration = typo.textDecoration.trim();
	}
	const backgroundImage = tokenValue(
		(typo as { backgroundImage?: unknown }).backgroundImage
	);
	if (backgroundImage) {
		out.backgroundImage = backgroundImage;
	}
	if (typeof typo.backgroundClip === "string" && typo.backgroundClip.trim()) {
		out.backgroundClip = typo.backgroundClip.trim();
	}
	if (
		typeof typo.webkitBackgroundClip === "string" &&
		typo.webkitBackgroundClip.trim()
	) {
		out.WebkitBackgroundClip = typo.webkitBackgroundClip.trim();
	}
	if (
		typeof typo.webkitTextFillColor === "string" &&
		typo.webkitTextFillColor.trim()
	) {
		out.WebkitTextFillColor = typo.webkitTextFillColor.trim();
	}
	return out;
}

function typographyFlatFromNode(node: ElementNode): Record<string, unknown> {
	const rawStyles = (node as unknown as { styles?: Record<string, unknown> })
		.styles;
	const fromFlat: Record<string, unknown> = {};
	if (rawStyles && typeof rawStyles === "object") {
		for (const key of THEME_TYPOGRAPHY_IMPORTANT_PROPS) {
			const v = rawStyles[key];
			if (typeof v === "number" && key === "fontWeight") {
				fromFlat[key] = v;
			} else if (typeof v !== "number") {
				const scalar = readStyleScalar(v).trim();
				if (scalar) {
					fromFlat[key] = scalar;
				}
			}
		}
	}
	return { ...fromFlat, ...typographyFromTokenizedStyle(node.style) };
}

/**
 * Desktop (base) typography overrides as real CSS with `!important`.
 * React `style={{…}}` cannot apply `!important`; Theme Manager tag rules can.
 */
export function collectElementTypographyOverridesCss(
	root: ElementNode[]
): string {
	const blocks: string[] = [];
	walkNodes(root, (node) => {
		const decl = typographyImportantDeclarationBlock(
			typographyFlatFromNode(node)
		);
		const sel = pbNodeBeatThemeSelector(node.id);
		if (!(decl && sel)) {
			return;
		}
		blocks.push(`${sel}{${decl}}`);
	});
	return blocks.join("");
}
